import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ObjectSchema, InferType, object, string, array } from 'yup';
import FormStateModal from '../FormStateModal/FormStateModal';
import { FormProvider, useForm, useFieldArray, useWatch, } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import _ from 'lodash';
import { cleanCircularReferences } from '../util/helpers';
import { UseReportResponse } from '../hooks/useReport/useReport';
import { getDefaultReportFilter, ReportContext } from '../../context/ReportProvider';
import './filters.css';
import { Trash, Plus } from 'react-bootstrap-icons';
import { store } from '../../../../stores/store';
import { toJS } from 'mobx';
import FilterQueryForm from './FilterQueryForm';
import { operators, IQueryBuilderSelects } from './filterFormSetup';
import { getAllowedOperatorsForType } from '../util/propertyOperatorMap';
import { FilterOperator } from '../../FiltersDisplay/FilterOperation';
import { RedisCacheObject } from '../../../../provider/RedisProvider';
import { removeMemIdParam } from '../../../../utils/removeMemIdParams';
// Need to update this to delineate some properties which are specific to the input "type"
// For instance, checkboxes and radios, or some inputs may have options, while others do not.
export type InputType<T,> = {
  id?: string;
  label: string;
  type: string;
  placeholder?: string;
  value?: string | number | null;
  name: keyof T;
  options?: Array<{ value: string | number; label: string }>;
  reportState?: UseReportResponse<unknown>;
}

/**
 * Props for the FilterForm component.
 */
export type FilterFormProps<T> = {
  inputs: InputType<T>[];
  schema: ObjectSchema<object>;
  onSubmit: (data: object) => Promise<void>;
  onError?: (errors: unknown, e: unknown) => void;
  defaultValues?: Partial<T>; // consider revisiting if we don't want to force every property to be in the defaultValues
  children?: React.ReactNode;
  loading?: boolean;
  reportState?: unknown; // TODO: Define the type for reportState
  queryBuilderFields: IQueryBuilderSelects;
};

/**
 * A reusable form component that integrates with React Hook Form and Yup validation.
 *
 * @component
 * @param {InputType[]} inputs - The JSON to generate inputs from
 * @param {ObjectSchema<object>} schema - The Yup schema for form validation.
 * @param {(data: unknown) => Promise<void>} onSubmit - The callback function to handle form submission.
 * @param {(errors: unknown, e: unknown) => void} [onError] - The optional callback function to handle form submission errors.
 * @param {object} defaultValues - The default values for the form fields.
 * @param {React.ReactNode} children - The child components of the form.
 * @param {boolean} [loading=false] - Indicates whether the form is in a loading state.
 * @param {unknown} [reportState] - Optional report state for additional functionality.
 * @param {IQueryBuilderSelects[]} queryBuilderFields - The fields for the query builder section of the form.
 * @returns {JSX.Element} The rendered FilterForm component.
 */
const FilterForm = <T,>(props: FilterFormProps<T>) => {
  const {
    inputs,
    schema,
    onSubmit,
    onError = () => { },
    children,
    loading = false,
    queryBuilderFields,
  } = props;

  // Get our store for redis info. We need the hospitals and departments from this
  const { commonStore } = store;

  const [queryParams, setQueryParams] = useSearchParams();
  const memId = queryParams.get('memid') || false;

  const redisCache = toJS(commonStore.redisCache) as RedisCacheObject;

  const initialParams = {
    departmentId: [commonStore.departmentId].filter((id): id is number => id !== undefined),
    hospitalId: [commonStore.hospitalId].filter((id): id is number => id !== undefined),
  };

  // Create a new schema that combines the query builder schema with the provided schema
  // This allows us to use the standard/global inputs alongside the other form inputs which will be specific to the form instance
  interface StandardFormShape {
    queryBuilder: {
      fieldName: string;
      operator: string;
      value?: string;
    }[];
    orderBy?: string;
    hospitalId: string[];
    departmentId: string[];
  }

  // This is the validation schema, based off our form shape
  // This will be combined with the schema provided as a prop which should cover the inputs specific to that report's needs on top of this
  const numericOps = ['greaterThan', 'lessThan', 'greaterThanEqual', 'lessThanEqual', 'equals', 'notEquals'];
  const noValueOps = React.useMemo(() => ['isEmpty', 'isNotEmpty', 'isNull', 'isNotNull'], []);

  const standardSchema: ObjectSchema<StandardFormShape> = object({
    queryBuilder: array().of(object({
      fieldName: string().required('Field name is required'),
      operator: string().required('Operator is required'),
      value: string()
        .when(['operator', 'fieldName'], (deps: unknown, schema) => {
          const [op, fieldName] = (Array.isArray(deps) ? deps : [undefined, undefined]) as [unknown, unknown];
          const opStr = String(op ?? '').trim().toLowerCase();
          const isNoValueOp = ['isempty', 'isnotempty', 'isnull', 'isnotnull'].includes(opStr);
          const isNumericOp = ['greaterthan', 'lessthan', 'greaterthanequal', 'lessthanequal', 'equals', 'notequals'].includes(opStr);
          const isListOp = ['in', 'notin'].includes(opStr);

          // Determine field type from queryBuilderFields
          const fieldType = queryBuilderFields?.data?.find(f => f.value === fieldName)?.type ?? 'string';

          if (isNoValueOp) {
            return schema
              .strip(true)
              .nullable()
              .notRequired()
              .transform(() => '')
              .test('must-be-empty', '', v => v === '' || v == null);
          }

          if (fieldType === 'number') {
            if (isListOp) {
              // Defensive: list operators should not be used with numeric fields
              return schema.test('list-not-allowed', 'List operator not allowed for numeric fields', () => false as unknown as boolean);
            }
            return schema
              .required('Value is required')
              .matches(/^[-+]?\d*(?:\.\d+)?$/, 'Enter a valid number');
          }

          if (isListOp) {
            return schema
              .required('Provide a list')
              .test(
                'has-list',
                'Provide a comma or space-separated list',
                v => !!v && v.split(/[\s,]+/).filter(Boolean).length > 0
              );
          }

          // Default string value required for text/eq-style operators
          return schema.required('Value is required');
        }),
    })).nullable().required(),
    hospitalId: array()
      .of(string().required('Hospital id is required'))
      .min(1, 'Select at least one hospital')
      .required('At least one hospital is required'),
    departmentId: array()
      .of(string().required('Department id is required'))
      .min(1, 'Select at least one department')
      .required('At least one department is required'),
    orderBy: string(),
  });

  const combinedSchema = standardSchema.concat(schema);
  type CombinedSchemaType = InferType<typeof combinedSchema>;

  // This should assist in handling memorization. We will populate the forms "defaultValues" based on URL parameters
  const hospitals = React.useMemo(() => redisCache?.Value?.Authorized?.Hospitals || [], [redisCache]);
  const departments = React.useMemo(() => (redisCache?.Value?.Authorized?.HospitalDepartments || []) as Array<{ HospitalId: string; DepartmentId: string; HospitalDepartmentName: string }>, [redisCache]);

  // Derive checkbox option lists without local state to avoid act() warnings in tests
  const hInputsOptions = React.useMemo(() => (
    hospitals?.map((hospital: any) => ({
      value: hospital.HospitalId,
      label: hospital.HospitalName,
    })) || []
  ), [hospitals]);
  const reportContext = React.useContext(ReportContext);
  const { memorizedData, reportFilter } = reportContext || { memorizedData: null, reportFilter: null };

  const isNonEmptyObject = (obj: unknown): obj is Record<string, unknown> => !!obj && typeof obj === 'object' && Object.keys(obj as Record<string, unknown>).length > 0;

  // Instantiate the form and pull out the methods for later use
  const methods = useForm<CombinedSchemaType>({
    defaultValues: (isNonEmptyObject(reportContext?.memorizedData)
      ? (reportContext?.memorizedData as CombinedSchemaType)
      : (isNonEmptyObject(reportContext?.reportFilter)
        ? (reportContext?.reportFilter as CombinedSchemaType)
        : getDefaultReportFilter(commonStore) as CombinedSchemaType)
    ),
    resolver: yupResolver(combinedSchema),
    mode: 'all',
  });
  const { control, register } = methods;
  // Watch queryBuilder rows to react to operator changes
  const queryBuilderValues = useWatch({ control, name: 'queryBuilder' });
  const watchHospitalIds = useWatch({ control, name: 'hospitalId' });
  const dInputsOptions = React.useMemo(() => (
    departments
      .filter(d => (watchHospitalIds || []).includes(d.HospitalId))
      .map((department: any) => ({
        value: department.DepartmentId,
        label: department.HospitalDepartmentName,
      })) || []
  ), [departments, watchHospitalIds]);

  const toStr = (v: string | number | null | undefined) => (v == null ? '' : String(v));

  // Setting up the field array for dynamic creation of new fields related to the query builder
  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: 'queryBuilder',
  });

  const hospitalToDepartmentIds = React.useMemo(() => {
    const map = new Map<string, string[]>();

    (departments ?? []).forEach(d => {
      const hId = toStr(d.HospitalId);
      const dId = toStr(d.DepartmentId);

      if (!map.has(hId)) {
        map.set(hId, []);
      }
      map.get(hId)!.push(dId);
    });

    return map;
  }, [departments]);

  const pruneDepartmentsForHospitals = React.useCallback(
    (hospitalIds: string[]) => {
      const allowedDeptIds = new Set<string>();

      hospitalIds.forEach(hId => {
        hospitalToDepartmentIds.get(hId)?.forEach(dId =>
          allowedDeptIds.add(dId)
        );
      });

      const currentDeptIds =
        ((methods.getValues('departmentId') as string[]) ?? []).map(toStr);

      const prunedDeptIds = currentDeptIds.filter(id =>
        allowedDeptIds.has(id)
      );

      if (prunedDeptIds.length !== currentDeptIds.length) {
        methods.setValue('departmentId' as keyof CombinedSchemaType, prunedDeptIds, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    },
    [hospitalToDepartmentIds, methods]
  );

  // Auto-clear value when operator expects no input
  useEffect(() => {
    (queryBuilderValues || []).forEach((row, idx) => {
      const op = row?.operator ?? '';
      if (noValueOps.includes(op)) {
        // Normalize to empty string only if it isn't already
        const currentVal = methods.getValues(`queryBuilder.${idx}.value` as any);
        if (currentVal !== '') {
          methods.setValue(`queryBuilder.${idx}.value` as any, '', {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }
        // Clear any lingering errors on the value field
        methods.clearErrors(`queryBuilder.${idx}.value` as any);
      }
    });

    if (!queryBuilderValues && Array.isArray(fields) && fields.length === 1) {
      methods.setValue('queryBuilder' as any, [] as CombinedSchemaType['queryBuilder']); // Reset to empty array if all queryBuilder fields were removed
    }

  }, [queryBuilderValues, methods, noValueOps]);

  // No effects for setting input configs; compute on the fly via useMemo

  useEffect(() => {
    const hasMem = isNonEmptyObject(memorizedData);
    const hasFilter = isNonEmptyObject(reportFilter);

    if (memId && hasMem) {
      methods.reset(memorizedData as any);
    } else if (hasFilter) {
      methods.reset(reportFilter as any);
      const obj = (reportFilter && typeof reportFilter === 'object')
        ? (reportFilter as Record<string, unknown>)
        : {};
      const isMissing = (v: unknown) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
      if (isMissing(obj.orderBy)) {
        methods.setValue('orderBy' as any, '');
      }
    } else {
      methods.reset(getDefaultReportFilter(commonStore) as any);
    }

    if (!hasFilter && hasMem && memId) {
      methods.handleSubmit(onSubmit, onError)();
    }
    // Only re-evaluate when memId or context payloads change; avoid resetting on unrelated renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memId, memorizedData, reportFilter]);


  return (
    <div id="filters-display-root" className={`p-5 bg-white position-fixed filters-pane ${reportContext?.filtersVisible ? 'open' : 'closed'}`}>

      {(commonStore.hospitalId && (memId && memorizedData && Object.keys(memorizedData).length > 0)) || !memId ? (
        <FormProvider {...methods}>
          <FormStateModal
            formState={{
              values: methods.watch(),
              ...methods.formState,
              errors: cleanCircularReferences(methods.formState.errors),
              isDirty: methods.formState.isDirty,
              isSubmitting: methods.formState.isSubmitting,
              // errors: methods.formState.errors, // Circular reference
              touchedFields: methods.formState.touchedFields,
              submitCount: methods.formState.submitCount,
            }}
          />
          <h3 className="d-flex justify-content-between">Filters <button
            className='btn btn-secondary mb-3 faux-link'
            onClick={() => {
              const currentValues = methods.getValues() as any;
              // Always reset Hospital/Department to logged-in defaults (Clear All behavior)
              const keepHospitalIds = [initialParams.hospitalId[0]?.toString()].filter(Boolean);
              const keepDepartmentIds = [initialParams.departmentId[0]?.toString()].filter(Boolean);

              const cleared: Record<string, any> = {};
              Object.entries(currentValues || {}).forEach(([key, value]) => {
                if (key === 'hospitalId') {
                  cleared[key] = keepHospitalIds;
                } else if (key === 'departmentId') {
                  cleared[key] = keepDepartmentIds;
                } else if (key === 'orderBy') {
                  cleared[key] = '';
                } else if (key === 'queryBuilder') {
                  cleared[key] = [];
                } else if (Array.isArray(value)) {
                  cleared[key] = [];
                } else if (typeof value === 'string') {
                  cleared[key] = '';
                } else if (typeof value === 'number' || typeof value === 'boolean') {
                  cleared[key] = undefined;
                } else if (value && typeof value === 'object') {
                  cleared[key] = {};
                } else {
                  cleared[key] = value;
                }
              });

              methods.reset(cleared as any);

              const updatedParams = removeMemIdParam(queryParams);
              setQueryParams(updatedParams);
              // Persist cleared state (preserving hospital/department) to context so it survives navigation
              reportContext?.setReportFilter({
                hospitalId: keepHospitalIds,
                departmentId: keepDepartmentIds,
                orderBy: '',
                queryBuilder: [],
              });

              if (reportContext?.setMemorizedData) {
                reportContext.setMemorizedData({
                  hospitalId: keepHospitalIds,
                  departmentId: keepDepartmentIds,
                  orderBy: '',
                  queryBuilder: [],
                });
              }
              reportContext?.setReportCurrentPageNumber(1);
            }}
            aria-label='Toggle Filters'
            title='Toggle Filters'
          >
            <Trash /> Clear Filters
          </button></h3>
          <form
            onSubmit={methods.handleSubmit(async (data) => {
              // Coerce numeric field values to numbers before submit
              const qb = ((data as any)?.queryBuilder || []) as Array<{ fieldName: string; operator: string; value?: any }>;
              const coerced = qb.map((row) => {
                const fType = queryBuilderFields?.data?.find(f => f.value === row.fieldName)?.type ?? 'string';
                if (fType === 'number' && row.value !== undefined && row.value !== null && row.value !== '') {
                  const num = Number(row.value);
                  return { ...row, value: isNaN(num) ? row.value : num };
                }
                return row;
              });
              const payload = { ...(data as any), queryBuilder: coerced };
              await onSubmit(payload);
              // Close the filters pane after a successful user-initiated submit
              reportContext?.setFiltersVisible(false);
            }, (errors, e) => {
              onError(errors, e);
            })}
            className={`${loading ? 'loading is-loading' : 'active'} ${methods.formState.isSubmitting ? 'is-submitting' : ''
              }`}
          >
            {/* In case we need to prepend the form with anything */}
            {children}

            {/* Map through the inputs provided as a prop (externally determined) and create the form fields */}

            {([
              { label: 'Hospital Name', type: 'checkbox', placeholder: 'Hospital Name', name: 'hospitalId', options: hInputsOptions } as InputType<CombinedSchemaType>,
              { label: 'Department Name', type: 'checkbox', placeholder: 'Department Name', name: 'departmentId', options: dInputsOptions } as InputType<CombinedSchemaType>,
              ...inputs,
            ]).map((input) => {
              if (input) {
                const { label, type, placeholder, options, name } = input;
                const id = input.id ? String(input.id) : String(name);
                const fieldValue = methods.getValues(name as keyof CombinedSchemaType);

                return (
                  <div key={id} className='form-group'>
                    <label htmlFor={id}>{label}</label>
                    {type === 'select' ? (
                      (() => {
                        const reg = methods.register(name as keyof CombinedSchemaType);
                        // Ensure the select reflects the current form value; undefined -> '' to show "Select"
                        const current = toStr(fieldValue as any);
                        return (
                          <select
                            id={id}
                            {...reg}
                            className="form-control"
                            value={current}
                          >
                            {options?.map((option) => (
                              <option
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </option>
                            ))}
                          </select>
                        );
                      })()
                    ) : ['radio', 'fancy-radio'].includes(type) && options ? (
                      <div className={`${type}-wrapper`} role="radiogroup" aria-label={label}>
                        {options.map((option) => {
                          const valStr = toStr(option.value);
                          const isChecked = toStr(fieldValue as any) === valStr;
                          return (
                            <div key={option.value} className={`form-check ${isChecked ? 'checked' : ''}`}>
                              <input
                                id={`${id}-${option.value}`}
                                type="radio"
                                {...methods.register(name as keyof CombinedSchemaType)}
                                value={valStr}
                                onChange={() =>
                                  methods.setValue(name as any, valStr, {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true,
                                  })
                                }
                                className="form-check-input"
                              />
                              <label htmlFor={`${id}-${option.value}`} className="form-check-label">
                                {option.label}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    ) : type === 'checkbox' && options ? (
                      <div className="form-check-group">
                        {(() => {
                          const isHospitalGroup = String(name) === 'hospitalId';
                          const isDepartmentGroup = String(name) === 'departmentId';
                          const optionValues = options.map(o => toStr(o.value));
                          const currentValues = ((methods.getValues(name as any) as string[]) || []).map(toStr);
                          const allSelected = optionValues.length > 0 && optionValues.every(v => currentValues.includes(v));
                          const noneSelected = currentValues.length === 0;
                          const someSelected = !allSelected && !noneSelected && currentValues.some(v => optionValues.includes(v));

                          const selectAllLabel = isHospitalGroup ? 'Select All Hospitals' : (isDepartmentGroup ? 'Select All Departments' : 'Select All');

                          const handleSelectAllToggle = () => {
                            if (!allSelected) {
                              methods.setValue(name as any, optionValues, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                            } else {
                              // Maintain at least one selection by falling back to logged-in defaults
                              if (isHospitalGroup) {
                                const fallback = [initialParams.hospitalId[0]?.toString()].filter(Boolean) as string[];
                                const next = fallback.length > 0 ? fallback : [];
                                methods.setValue(name as any, next, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                              } else if (isDepartmentGroup) {
                                const fallback = [initialParams.departmentId[0]?.toString()].filter(Boolean) as string[];
                                const next = fallback.length > 0 ? fallback : [];
                                methods.setValue(name as any, next, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                              } else {
                                methods.setValue(name as any, [], { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                              }
                            }
                          };

                          return (
                            <div className="form-check mb-2">
                              <input
                                id={`${id}-select-all`}
                                type="checkbox"
                                className="form-check-input"
                                checked={allSelected}
                                ref={el => { if (el) el.indeterminate = someSelected; }}
                                onChange={handleSelectAllToggle}
                              />
                              <label htmlFor={`${id}-select-all`} className="form-check-label">
                                {selectAllLabel}
                              </label>
                            </div>
                          );
                        })()}
                        {options.map((option) => {
                          const reg = methods.register(name as keyof CombinedSchemaType);
                          const optionValueStr = toStr(option.value);

                          const isHospitalGroup = String(name) === 'hospitalId';

                          return (
                            <div key={option.value} className="form-check">
                              <input
                                id={`${id}-${option.value}`}
                                type="checkbox"
                                value={optionValueStr}
                                className="form-check-input"
                                {...reg}
                                onChange={(e) => {
                                  reg.onChange(e);

                                  if (isHospitalGroup) {
                                    queueMicrotask(() => {
                                      const nextHospitals =
                                        ((methods.getValues('hospitalId') as string[]) ?? []).map(toStr);

                                      pruneDepartmentsForHospitals(nextHospitals);
                                    });
                                  }
                                }}
                              />
                              <label htmlFor={`${id}-${option.value}`} className='form-check-label'>
                                {option.label}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <input
                        id={id}
                        type={type}
                        placeholder={placeholder}
                        className='form-control'
                        {...methods.register(name as keyof CombinedSchemaType)}
                      />
                    )}
                  </div>
                );
              }
              return null;
            })}

            <FilterQueryForm />

            {/* Query Builder Section — "fields" name comes from useFieldArray */}
            {Array.isArray(queryBuilderValues) && queryBuilderValues.length > 0 && (
              <>
                {fields.map((field, index) => {
                  // Get selected field and its type
                  const selectedField = queryBuilderFields.data.find(f => f.value === queryBuilderValues?.[index]?.fieldName);
                  const propertyType = selectedField?.type || 'string';
                  const allowedOps = getAllowedOperatorsForType(propertyType);
                  return (
                    <div key={field.id} className='form-group d-flex align-items-center'>
                      {/* Field selector */}
                      <select
                        className='form-control me-2'
                        {...register(`queryBuilder.${index}.fieldName`)}
                      >
                        <option value=''>Select Field Name</option>
                        {queryBuilderFields.data.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {/* Operator selector, filtered by property type */}
                      <select
                        className='form-control me-2'
                        {...register(`queryBuilder.${index}.operator`)}
                      >
                        <option value=''>Select Operator</option>
                        {operators.data
                          .filter((option) => allowedOps.includes(option.value as FilterOperator))
                          .map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                      </select>

                      <input
                        id={`queryBuilder-${index}-value`}
                        type={(propertyType === 'number' && !noValueOps.includes(queryBuilderValues?.[index]?.operator ?? '')) ? 'number' : 'text'}
                        placeholder={
                          noValueOps.includes(queryBuilderValues?.[index]?.operator ?? '')
                            ? 'No value required'
                            : 'Value'
                        }
                        className='form-control'
                        disabled={noValueOps.includes(queryBuilderValues?.[index]?.operator ?? '')}
                        inputMode={(propertyType === 'number' && !noValueOps.includes(queryBuilderValues?.[index]?.operator ?? '')) ? 'decimal' : undefined}
                        step={(propertyType === 'number' && !noValueOps.includes(queryBuilderValues?.[index]?.operator ?? '')) ? 'any' : undefined}
                        {...register(`queryBuilder.${index}.value`)}
                      />
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={() => {
                          remove(index);
                        }}
                      >
                        <Trash />
                      </button>
                    </div>
                  );
                })}
              </>)}
            <button
              type="button"
              className="btn btn-light"
              onClick={async () => {
                // Validate the last queryBuilder row before allowing a new one
                const lastIndex = fields.length - 1;
                if (lastIndex >= 0) {
                  const paths = [
                    `queryBuilder.${lastIndex}.fieldName`,
                    `queryBuilder.${lastIndex}.operator`,
                    `queryBuilder.${lastIndex}.value`,
                  ];
                  const ok = await methods.trigger(paths as any, { shouldFocus: true } as any);
                  if (!ok) {
                    return; // Block adding another row until current is valid
                  }
                }
                append({ fieldName: '', operator: '', value: '' }); // Append a new field to the query builder
              }}
            >
              <Plus />
            </button>

            {/* Display global form errors */}

            {/* Only show global errors if not only empty value errors for noValueOps */}
            {!_.isEmpty(methods.formState.errors) &&
              Object.entries(methods.formState.errors).some(([key, value]) => {
                if (key === 'queryBuilder' && Array.isArray(value)) {
                  // Check if any error in queryBuilder worth showing (exclude no-value ops)
                  return value.some((rowErr: any, idx: number) => {
                    const op = String(queryBuilderValues?.[idx]?.operator ?? '').trim().toLowerCase();
                    const isNoVal = ['isempty', 'isnotempty', 'isnull', 'isnotnull'].includes(op);
                    return !isNoVal && rowErr && rowErr.value && rowErr.value.message;
                  });
                }
                return value && value.message;
              }) ? (
              <div className='alert alert-danger col-12' role='alert'>
                <h3>Global Form Errors:</h3>
                {Object.entries(methods.formState.errors).map(([key, value]) => {
                  if (key === 'queryBuilder' && Array.isArray(value)) {
                    return value.map((rowErr: any, idx: number) => {
                      const op = String(queryBuilderValues?.[idx]?.operator ?? '').trim().toLowerCase();
                      const isNoVal = ['isempty', 'isnotempty', 'isnull', 'isnotnull'].includes(op);
                      return (!isNoVal && rowErr && rowErr.value && rowErr.value.message)
                        ? <p key={key + idx}>{rowErr.value.message}</p>
                        : null;
                    });
                  }
                  return value && value.message ? <p key={key}>{value.message.toString()}</p> : null;
                })}
              </div>
            ) : null}

            <br />

            <button
              type='submit'
              className='btn btn-primary'
              disabled={methods.formState.isSubmitting}
            >
              Submit
            </button>
          </form>
        </FormProvider>
      ) : (
        <div className='alert alert-warning'>
          <h3>Form data loading</h3>
        </div>
      )}
    </div >
  );
};

export default FilterForm;
